import React, { useState } from 'react';
import { UserPlus, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Navbar } from '../Header/Navbar';
import { MemberPicker } from '../Forms/MemberPicker';
import { useFamilyStore } from '../../store/familyStore';
import { getAdapter } from '../../lib/adapters';
import type { SubmissionRelationship } from '../../lib/adapters';
import { pushRoute, currentRoute } from '../../utils/routing';

type Gender = 'male' | 'female' | 'other';
type MaritalStatus = 'single' | 'married' | 'divorced' | 'widowed';
type RelType = 'child' | 'spouse' | 'sibling' | 'parent';

const EMPTY_FORM = {
  name: '',
  nickname: '',
  birthDate: '',
  birthPlace: '',
  currentLocation: '',
  profession: '',
  education: '',
  gender: 'male' as Gender,
  maritalStatus: 'single' as MaritalStatus,
  isAlive: true,
  notes: '',
};

export const SubmitMember: React.FC = () => {
  // /submit/:treeId — parse the tree id from the current route.
  const match = currentRoute().match(/^\/submit\/([^/]+)/);
  const treeId = match?.[1] ?? '';
  const { members, fetchMembers, isLoading } = useFamilyStore();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [relType, setRelType] = useState<RelType | ''>('');
  const [relatedMemberId, setRelatedMemberId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const turnstileRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (treeId) fetchMembers(treeId);
  }, [treeId, fetchMembers]);

  // Load the Turnstile script and render the widget once.
  React.useEffect(() => {
    if (!turnstileSiteKey) return;

    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileSiteKey,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
          'error-callback': () => setTurnstileToken(null),
        });
      }
    };

    if (document.querySelector('script[data-turnstile]') && window.turnstile) {
      renderWidget();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = 'true';
    script.onload = () => {
      // Wait a tick for the Turnstile global to initialize.
      setTimeout(renderWidget, 50);
    };
    document.head.appendChild(script);
    return () => { script.onload = null; };
  }, [turnstileSiteKey]);

  const set = (patch: Partial<typeof form>) => setForm(f => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const name = form.name.trim();
    if (name.length < 2) {
      setError('Please enter the person\u2019s full name (at least 2 characters).');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security check before submitting.');
      return;
    }

    let relationship: SubmissionRelationship | undefined;
    if (relType && relatedMemberId) {
      relationship = { type: relType, relatedMemberId };
    }

    setSubmitting(true);
    try {
      const adapter = getAdapter();
      await adapter.createSubmission({
        treeId,
        name,
        nickname: form.nickname.trim() || undefined,
        birthDate: form.birthDate || undefined,
        birthPlace: form.birthPlace.trim() || undefined,
        currentLocation: form.currentLocation.trim() || undefined,
        profession: form.profession.trim() || undefined,
        education: form.education.trim() || undefined,
        gender: form.gender,
        maritalStatus: form.maritalStatus,
        isAlive: form.isAlive,
        notes: form.notes.trim() || undefined,
        relationship,
        turnstileToken,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-16 flex flex-col items-center text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Thank you!</h1>
          <p className="text-gray-600 mb-8">
            Your submission for <span className="font-medium text-gray-900">{form.name}</span> has been
            received. Our team will review it and add them to the family tree.
          </p>
          <button
            onClick={() => pushRoute(`/family-tree/${treeId}`)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-700 text-white font-semibold rounded-lg hover:bg-green-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to the family tree
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-green-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suggest a Family Member</h1>
            <p className="text-sm text-gray-500">
              Fill in what you know — the more, the better. We\u2019ll review and add them to the tree.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          {/* Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => set({ name: e.target.value })}
              onBlur={() => setNameTouched(true)}
              placeholder="e.g. Fatema Begum"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
            {nameTouched && form.name.trim().length < 2 && (
              <p className="text-red-500 text-xs mt-1">Name must be at least 2 characters.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
              <input
                type="text"
                value={form.nickname}
                onChange={e => set({ nickname: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={e => set({ gender: e.target.value as Gender })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 bg-white"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
              <input
                type="date"
                value={form.birthDate}
                onChange={e => set({ birthDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Birth Place</label>
              <input
                type="text"
                value={form.birthPlace}
                onChange={e => set({ birthPlace: e.target.value })}
                placeholder="e.g. Dhaka, BD"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Location</label>
              <input
                type="text"
                value={form.currentLocation}
                onChange={e => set({ currentLocation: e.target.value })}
                placeholder="e.g. Chattogram, BD"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select
                value={form.maritalStatus}
                onChange={e => set({ maritalStatus: e.target.value as MaritalStatus })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 bg-white"
              >
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
              <input
                type="text"
                value={form.profession}
                onChange={e => set({ profession: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
              <input
                type="text"
                value={form.education}
                onChange={e => set({ education: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isAlive}
                onChange={e => set({ isAlive: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded"
              />
              Currently alive
            </label>
            {!form.isAlive && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Death Date</label>
                <input
                  type="date"
                  value={form.deathDate ?? ''}
                  onChange={e => set({ deathDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set({ notes: e.target.value })}
              placeholder="Any other details you\u2019d like to share…"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>

          {/* Optional relationship */}
          <div className="border-t border-gray-100 pt-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">How does this person relate to the tree? <span className="text-gray-400 font-normal">(optional)</span></h3>
            <p className="text-xs text-gray-500 mb-4">
              This helps us place them correctly. Pick a relationship to an existing member.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <select
                  value={relType}
                  onChange={e => setRelType(e.target.value as RelType | '')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 bg-white"
                >
                  <option value="">None</option>
                  <option value="child">Child of…</option>
                  <option value="spouse">Spouse of…</option>
                  <option value="sibling">Sibling of…</option>
                  <option value="parent">Parent of…</option>
                </select>
              </div>
              <MemberPicker
                members={members}
                value={relatedMemberId}
                onChange={setRelatedMemberId}
                label="Related to"
                placeholder={isLoading ? 'Loading members…' : 'Search a member…'}
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
          )}

          {/* Cloudflare Turnstile anti-spam */}
          {turnstileSiteKey && (
            <div className="flex justify-center">
              <div ref={turnstileRef} />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-green-700 text-white font-semibold rounded-lg hover:bg-green-800 transition-colors disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </form>
      </main>
    </div>
  );
};
